/* VALRSOUL：购物车恢复 + 商品页后台满赠结账 2026-10-17。 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';
import {
  VERSION, isGift, merchandiseSubtotal, thresholdsFor, lineAddress,
  productGroups, reconcileGifts, changeGroup,
} from './valr-rewards-core.js?v=202610290353-whole-thresholds';

// Advertised gift values in USD cents, ordered ring / necklace / bracelet.
const GIFT_VALUES_USD_CENTS = Object.freeze([2500, 3000, 7000]);

const checkoutSelector = 'button[name="checkout"],input[name="checkout"],a[href]';
const TIMEOUTS = { request: 15000, native: 15000, lock: 10000, job: 45000, checkout: 60000 };

function cartError(message, code = 'CART_INTERRUPTED') {
  const error = new Error(message);
  error.code = code;
  return error;
}

function checkSignal(signal) {
  if (signal?.aborted) throw signal.reason || cartError('Cart update interrupted.');
}

// Observing a promise with a deadline does not cancel its producer. Every cart
// write also checks the operation signal, so late completions cannot start a new write.
function waitFor(promise, signal) {
  if (signal?.aborted) return Promise.reject(signal.reason || cartError('Cart update interrupted.'));
  if (!signal) return Promise.resolve(promise);
  return new Promise((resolve, reject) => {
    const abort = () => reject(signal.reason || cartError('Cart update interrupted.'));
    signal.addEventListener('abort', abort, { once: true });
    Promise.resolve(promise).then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
  });
}

function deadline(milliseconds, code, parent) {
  const controller = new AbortController();
  const abort = () => controller.abort(parent.reason || cartError('Cart update interrupted.'));
  if (parent?.aborted) abort();
  else parent?.addEventListener('abort', abort, { once: true });
  const timer = setTimeout(() => controller.abort(cartError('Cart update timed out.', code)), milliseconds);
  return {
    controller,
    signal: controller.signal,
    dispose() { clearTimeout(timer); parent?.removeEventListener('abort', abort); },
  };
}

class RewardsManager {
  constructor(config) {
    this.config = config;
    document.documentElement.classList.add('valr-rewards-enabled');
    this.result = null;
    this.busy = false;
    this.error = '';
    this.pending = new Set();
    this.tail = Promise.resolve();
    this.scheduled = false;
    this.refreshRequested = false;
    this.recoveryRequired = false;
    this.activeController = null;
    this.busyTimer = null;
    this.ownEvents = new WeakSet();
    this.revision = 0;
    this.checkoutRequested = false;
    this.directBuyIntent = null;
    this.views = new Set();
    this.tasks = 0;
    this.startScheduled = false;
    this.channel = null;
    try {
      if (typeof BroadcastChannel === 'function') this.channel = new BroadcastChannel('valrsoul-cart-rewards');
    } catch { /* Restricted browsers still support the single-tab cart. */ }
    this.channel?.addEventListener('message', () => this.schedule());
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => this.nativeUpdate(event));
    document.addEventListener(StandardEvents.cartDiscountUpdate, (event) => this.nativeUpdate(event));
    document.addEventListener('click', (event) => this.click(event), true);
    document.addEventListener('change', (event) => this.change(event), true);
    document.addEventListener('submit', (event) => this.submit(event), true);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && event.target.matches?.('[data-valr-quantity]')) {
        event.preventDefault();
        event.target.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }, true);
    window.addEventListener('pageshow', (event) => { if (event.persisted) this.schedule(); });
    window.addEventListener('pagehide', () => {
      this.activeController?.abort(cartError('Page left during a cart update.', 'PAGE_LEFT'));
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.views.size) this.schedule();
    });
  }

  mount(view, config) {
    this.views.add(view);
    if (config.currency !== this.config.currency || config.country !== this.config.country) {
      this.config = config;
      this.result = null;
      this.schedule();
    }
    this.paintView(view);
    if (!this.startScheduled) {
      this.startScheduled = true;
      if (Number(view.dataset.cartCount) > 0) this.schedule();
      else this.paint();
    } else if (!this.result && !this.busy && Number(view.dataset.cartCount) > 0) {
      this.schedule();
    }
  }

  root() {
    return window.Shopify?.routes?.root || this.config.root || '/';
  }

  verifyBuyNowCart(cart, intent = this.directBuyIntent) {
    if (!intent) return;
    const items = cart.items.filter((item) => !isGift(item, this.config));
    if (!items.length || items.some((item) => String(item.variant_id) !== intent.variantId) ||
        items.reduce((sum, item) => sum + item.quantity, 0) !== intent.quantity ||
        (intent.currency && cart.currency !== intent.currency)) {
      throw cartError('The cart no longer matches the selected product.', 'DIRECT_CART_CHANGED');
    }
  }

  async request(path, body, signal) {
    checkSignal(signal);
    const url = new URL(this.root() + path, location.origin);
    if (url.origin !== location.origin) throw new Error('Invalid cart URL.');
    const timeout = deadline(TIMEOUTS.request, 'REQUEST_TIMEOUT', signal);
    try {
      const response = await waitFor(fetch(url, {
        method: body ? 'POST' : 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {}),
        signal: timeout.signal,
      }), timeout.signal);
      const data = await waitFor(response.json(), timeout.signal);
      checkSignal(signal);
      if (!response.ok || data.errors || Number(data.status) >= 400) {
        const error = new Error(typeof data.description === 'string' ? data.description : 'The cart could not be updated.');
        error.status = response.status;
        throw error;
      }
      return data;
    } finally { timeout.dispose(); }
  }

  api(signal) {
    let snapshotRevision = this.revision;
    const ensureStable = async () => {
      await this.waitNative(signal);
      checkSignal(signal);
      if (snapshotRevision !== this.revision) {
        const error = new Error('Another cart update changed these lines.');
        error.code = 'STALE_CART';
        throw error;
      }
    };
    return {
      read: async () => {
        await this.waitNative(signal);
        snapshotRevision = this.revision;
        return this.request('cart.js', undefined, signal);
      },
      change: async (cart, item, quantity) => {
        await ensureStable();
        return this.request('cart/change.js', { ...lineAddress(cart, item), quantity }, signal);
      },
      add: async (items) => {
        await ensureStable();
        return this.request('cart/add.js', { items }, signal);
      },
    };
  }

  async waitNative(signal) {
    checkSignal(signal);
    while (this.pending.size) {
      await waitFor(Promise.allSettled([...this.pending]), signal);
      if (this.recoveryRequired) throw cartError('Reload the cart before another update.');
      checkSignal(signal);
    }
    if (this.recoveryRequired) throw cartError('Reload the cart before another update.');
  }

  nativeUpdate(event) {
    if (this.ownEvents.has(event)) return;
    this.revision += 1;
    if (event.promise) {
      const timeout = deadline(TIMEOUTS.native, 'NATIVE_TIMEOUT');
      const promise = waitFor(event.promise, timeout.signal);
      this.pending.add(promise);
      this.setBusy(true);
      promise.catch((error) => {
        // A timed-out native write may still reach Shopify. Require a fresh page
        // instead of replaying it or enabling another write on an unknown cart.
        if (timeout.signal.aborted) this.fail(error);
      }).finally(() => {
        timeout.dispose();
        this.pending.delete(promise);
        if (!this.recoveryRequired) this.schedule();
        this.setBusy(this.tasks > 0 || this.pending.size > 0 || this.scheduled);
      });
    } else this.schedule();
  }

  schedule() {
    if (this.recoveryRequired) return;
    this.refreshRequested = true;
    this.setBusy(true);
    if (this.scheduled || this.tasks || this.pending.size) return;
    this.scheduled = true;
    setTimeout(() => {
      this.scheduled = false;
      if (this.recoveryRequired) return;
      if (this.tasks || this.pending.size) return;
      this.refreshRequested = false;
      this.enqueue();
    }, 0);
  }

  enqueue(operation, options = {}) {
    this.tasks += 1;
    this.setBusy(true);
    const execute = async () => {
      const timeout = deadline(TIMEOUTS.job, 'JOB_TIMEOUT', options.signal);
      this.activeController = timeout.controller;
      try {
        if (this.recoveryRequired) return false;
        return await this.run(operation, timeout.signal, options);
      } catch (error) {
        this.fail(error);
        return false;
      } finally {
        timeout.dispose();
        if (this.activeController === timeout.controller) this.activeController = null;
        this.tasks -= 1;
        if (this.refreshRequested && !this.tasks && !this.pending.size) this.schedule();
        this.setBusy(this.tasks > 0 || this.pending.size > 0 || this.scheduled);
      }
    };
    const job = this.tail.then(execute, execute);
    this.tail = job.catch(() => null);
    return job;
  }

  async withCartLock(operation, signal) {
    checkSignal(signal);
    if (!navigator.locks?.request) return operation();
    const timeout = deadline(TIMEOUTS.lock, 'LOCK_TIMEOUT', signal);
    try {
      return await navigator.locks.request('valrsoul-cart-rewards', { signal: timeout.signal }, () => {
        timeout.dispose(); // This deadline limits lock acquisition, not the protected write.
        checkSignal(signal);
        return operation();
      });
    } finally { timeout.dispose(); }
  }

  fail(error) {
    if (this.recoveryRequired) return;
    this.recoveryRequired = true;
    this.refreshRequested = false;
    this.error = error.code === 'DIRECT_CART_CHANGED'
      ? 'Your cart changed. Reload and review your items before checking out.'
      : 'Your cart update was interrupted. Reload your cart to continue.';
    this.result = null;
    this.activeController?.abort(error);
    console.warn('[VALRSOUL rewards]', error.code || error.name, error.message);
    this.setBusy(false);
  }

  recover() {
    const url = new URL(this.root() + 'cart', location.origin);
    location.assign(url.href);
  }

  async sections(signal) {
    const ids = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((element) => element.dataset.sectionId))].slice(0, 5);
    if (!ids.length) return {};
    const url = new URL(location.href);
    url.searchParams.set('sections', ids.join(','));
    url.searchParams.delete('section_id');
    const timeout = deadline(TIMEOUTS.request, 'DISPLAY_TIMEOUT', signal);
    try {
      const response = await waitFor(fetch(url, {
        credentials: 'same-origin', cache: 'no-store', signal: timeout.signal,
      }), timeout.signal);
      if (!response.ok) throw new Error('The cart display could not be refreshed.');
      const sections = await waitFor(response.json(), timeout.signal);
      if (ids.some((id) => typeof sections[id] !== 'string')) throw new Error('The cart display could not be refreshed.');
      return sections;
    } finally { timeout.dispose(); }
  }

  async publish(cart, signal, revision) {
    const sections = await this.sections(signal);
    checkSignal(signal);
    if (revision !== this.revision) throw cartError('Cart changed while its display was refreshing.', 'STALE_CART');
    const components = [...document.querySelectorAll('cart-items-component[data-section-id]')];
    for (const [id, html] of Object.entries(sections)) {
      const component = components.find((element) => element.dataset.sectionId === id);
      checkSignal(signal);
      await waitFor(morphSection(id, html, { mode: component?.hasAttribute('data-drawer') ? 'hydration' : 'full' }), signal);
    }
    const deferred = CartLinesUpdateEvent.createPromise();
    const event = new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise });
    this.ownEvents.add(event);
    document.dispatchEvent(event);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections, items: cart.items, itemCount: cart.item_count, source: 'valrsoul-rewards' },
    });
    // Horizon's section morph is synchronous; its promise continuations need
    // microtasks, not animation frames (which stop in background tabs).
    await Promise.resolve();
    await Promise.resolve();
    checkSignal(signal);
  }

  async sync(operation, signal) {
    const api = this.api(signal);
    let latest = await api.read();
    if (operation) latest = await operation(latest, api);
    for (let pass = 0; pass < 5; pass += 1) {
      await this.waitNative(signal);
      const revision = this.revision;
      this.verifyBuyNowCart(latest);
      const thresholds = thresholdsFor(latest, this.config, window.Shopify);
      const result = await reconcileGifts(latest, this.config, thresholds, api);
      this.verifyBuyNowCart(result.cart);
      await this.waitNative(signal);
      checkSignal(signal);
      if (revision === this.revision) return { ...result, revision };
      latest = await api.read();
    }
    throw cartError('Cart changed repeatedly while updating.', 'STALE_CART');
  }

  async run(operation, signal, options = {}) {
    this.error = '';
    try {
      // Never hold the cross-tab lock while rendering, painting, or waiting for UI.
      const result = await this.withCartLock(async () => {
        if (options.prepare) await options.prepare(signal);
        return this.sync(operation, signal);
      }, signal);
      checkSignal(signal);
      if (operation || options.prepare || result.changed) this.channel?.postMessage({ changed: true });
      this.result = result;
      if (options.render !== false) await this.publish(result.cart, signal, result.revision);
      if (this.pending.size) await this.waitNative(signal);
      this.paint();
      return true;
    } catch (error) {
      // Re-read after a concurrent native change, but never replay a user mutation.
      if (error.code === 'STALE_CART' && !signal?.aborted && !this.recoveryRequired) this.schedule();
      else this.fail(error);
      return false;
    }
  }

  setBusy(value) {
    this.busy = Boolean(value) && !this.recoveryRequired;
    // The deadline spans the whole queue, including repeated stale-cart retries.
    if (this.busy && !this.busyTimer) {
      this.busyTimer = setTimeout(() => {
        this.busyTimer = null;
        this.fail(cartError('Cart updates did not settle.', 'BUSY_TIMEOUT'));
      }, TIMEOUTS.checkout);
    } else if (!this.busy && this.busyTimer) {
      clearTimeout(this.busyTimer);
      this.busyTimer = null;
    }
    document.documentElement.classList.toggle('valr-rewards-busy', this.busy);
    this.paint();
  }

  format(cents, currency = this.result?.cart.currency || this.config.currency, compactIntegers = false) {
    // Compact whole-unit reward labels; preserve precision for cart prices,
    // remaining spend and explicit threshold overrides.
    const digits = (compactIntegers && cents % 100 === 0) || ['JPY', 'KRW', 'VND', 'CLP'].includes(currency) ? 0 : 2;
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency, minimumFractionDigits: digits, maximumFractionDigits: digits,
    }).format(cents / 100);
  }

  giftValueLabel(giftIndex, currency) {
    const usdCents = GIFT_VALUES_USD_CENTS[giftIndex];
    if (!Number.isSafeInteger(usdCents) || this.config.baseCurrency !== 'USD') return '';
    let rate = 1;
    if (currency !== this.config.baseCurrency) {
      const current = window.Shopify?.currency;
      rate = Number(current?.rate);
      if (current?.active !== currency || !Number.isFinite(rate) || rate <= 0) return '';
    }
    // Convert the original USD value, then round UP to a whole currency unit.
    // The small tolerance prevents floating-point noise from adding a full unit.
    const convertedCents = Math.ceil((usdCents * rate - 1e-7) / 100) * 100;
    if (!Number.isSafeInteger(convertedCents) || convertedCents <= 0) return '';
    return `${this.format(convertedCents, currency, true)} Value`;
  }

  paintView(view) {
    const status = view.querySelector('[data-valr-status]');
    const detail = view.querySelector('[data-valr-detail]');
    const retry = view.querySelector('[data-valr-retry]');
    const nodes = view.querySelectorAll('[data-valr-node]');
    if (!status || !detail) return;
    const giftCurrency = this.result?.cart.currency || this.config.currency;
    nodes.forEach((node, index) => {
      const giftValue = node.querySelector('.valr-reward-value');
      if (giftValue && index > 0) {
        giftValue.textContent = this.giftValueLabel(index - 1, giftCurrency);
      }
    });
    view.setAttribute('aria-busy', String(this.busy));
    retry.hidden = !this.error;
    if (this.error) {
      status.textContent = this.error;
      detail.textContent = 'Reload to get the latest cart before changing items or checking out.';
      return;
    }
    if (!this.result) {
      status.textContent = this.directBuyIntent ? 'Preparing your gifts and checkout…'
        : this.busy ? 'Updating your rewards…' : 'Your rewards update automatically as you shop.';
      detail.textContent = 'Standard shipping and gift goals use merchandise before discounts; gift items are excluded.';
      return;
    }
    const { cart, thresholds, tier, warning } = this.result;
    const gross = merchandiseSubtotal(cart, this.config);
    const unlocked = [gross >= thresholds.shipping, ...this.config.gifts.map((_, i) => i < tier)];
    const values = [thresholds.shipping, ...thresholds.gifts];
    nodes.forEach((node, index) => {
      node.classList.toggle('is-unlocked', unlocked[index]);
      node.querySelector('[data-valr-threshold]').textContent = this.format(values[index], cart.currency, true);
      node.querySelector('[data-valr-check]').textContent = unlocked[index] ? '✓' : String(index + 1);
      node.setAttribute('aria-label', `${node.dataset.label}: ${this.format(values[index], cart.currency, true)}${unlocked[index] ? ', unlocked' : ''}`);
      const base = index === 0 ? 0 : values[index - 1];
      const amount = gross;
      const fill = Math.max(0, Math.min(1, (amount - base) / (values[index] - base)));
      node.style.setProperty('--valr-node-fill', `${fill * 100}%`);
    });
    if (this.directBuyIntent) {
      status.textContent = 'Preparing your gifts and checkout…';
    } else if (this.busy) {
      status.textContent = 'Updating your rewards…';
    } else if (warning) {
      status.textContent = warning;
    } else if (!unlocked[0]) {
      status.textContent = `Add ${this.format(Math.max(0, thresholds.shipping - gross))} for FREE shipping.`;
    } else if (tier < 3) {
      const names = ['a FREE mystery ring', 'a FREE mystery necklace', 'a FREE mystery bracelet'];
      status.textContent = `Add ${this.format(Math.max(0, thresholds.gifts[tier] - gross))} for ${names[tier]}.`;
    } else {
      status.textContent = 'FREE shipping and all 3 mystery gifts unlocked!';
    }
    const shippingText = unlocked[0] ? 'FREE standard shipping unlocked.' : 'Standard shipping goal not yet reached.';
    detail.textContent = `${shippingText} All goals use merchandise before discounts; gift items are excluded.`;
  }

  paint() {
    this.views.forEach((view) => {
      if (!view.isConnected) this.views.delete(view);
      else this.paintView(view);
    });
    if (this.result && !this.busy) this.decorateRows();
  }

  decorateRows() {
    const cart = this.result.cart;
    const groups = productGroups(cart, this.config);
    document.querySelectorAll('cart-items-component').forEach((component) => {
      const rows = [...component.querySelectorAll('[data-valr-index]')];
      if (rows.length !== cart.items.length) return;
      if (rows.some((row, i) => String(row.dataset.valrVariant) !== String(cart.items[i].variant_id))) return;
      rows.forEach((row, index) => {
        row.hidden = false;
        row.classList.remove('valr-group-first', 'valr-group-hidden');
        row.querySelectorAll('[data-valr-controls],[data-valr-group-price],[data-valr-gift-label]').forEach((node) => node.remove());
        if (isGift(cart.items[index], this.config)) {
          row.classList.add('valr-gift-row');
          const label = document.createElement('span');
          label.dataset.valrGiftLabel = '';
          label.className = 'valr-gift-label';
          label.textContent = 'FREE GIFT · Qty 1';
          row.querySelector('.cart-items__quantity')?.append(label);
        }
      });
      groups.forEach((group) => {
        const first = rows[group.indices[0]];
        if (!first) return;
        first.classList.add('valr-group-first');
        group.indices.slice(1).forEach((index) => {
          rows[index].classList.add('valr-group-hidden');
          rows[index].hidden = true;
        });
        const controls = document.createElement('div');
        controls.className = 'valr-quantity-controls';
        controls.dataset.valrControls = '';
        controls.dataset.identity = group.identity;
        controls.dataset.quantity = String(group.quantity);
        const productTitle = group.items[0].product_title || group.items[0].title;
        const stepper = document.createElement('div');
        stepper.className = 'valr-quantity';
        for (const [action, label, text] of [['decrease', `Decrease quantity for ${productTitle}`, '−'], ['increase', `Increase quantity for ${productTitle}`, '+']]) {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.valrAction = action;
          button.setAttribute('aria-label', label);
          button.textContent = text;
          stepper.append(button);
          if (action === 'decrease') {
            const input = document.createElement('input');
            input.type = 'number'; input.min = '0'; input.max = '999'; input.step = '1';
            input.inputMode = 'numeric'; input.value = String(group.quantity);
            input.dataset.valrQuantity = '';
            input.setAttribute('aria-label', `Quantity for ${productTitle}`);
            stepper.append(input);
          }
        }
        controls.append(stepper);
        const remove = document.createElement('button');
        remove.type = 'button'; remove.className = 'valr-remove';
        remove.dataset.valrAction = 'remove';
        remove.setAttribute('aria-label', `Remove ${productTitle}`);
        const icon = first.querySelector('.cart-items__remove .svg-wrapper, .cart-items__remove svg');
        if (icon) remove.append(icon.cloneNode(true)); else remove.textContent = 'Remove';
        controls.append(remove);
        first.querySelector('.cart-items__quantity')?.append(controls);
        const price = document.createElement('span');
        price.dataset.valrGroupPrice = '';
        price.textContent = this.format(group.price, cart.currency);
        first.querySelector('.cart-items__price')?.append(price);
      });
    });
  }

  click(event) {
    if (!(event.target instanceof Element)) return;
    const action = event.target.closest('[data-valr-action]');
    if (action) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (this.recoveryRequired) { this.recover(); return; }
      if (this.busy) return;
      const controls = action.closest('[data-valr-controls]');
      const current = Number(controls.dataset.quantity);
      const target = action.dataset.valrAction === 'remove' ? 0 : current + (action.dataset.valrAction === 'increase' ? 1 : -1);
      const identity = controls.dataset.identity;
      this.enqueue((cart, api) => changeGroup(cart, this.config, identity, target, api));
      return;
    }
    if (event.target.closest('[data-valr-retry]')) {
      if (!this.recoveryRequired) {
        event.preventDefault(); event.stopImmediatePropagation(); this.schedule();
      }
      // Recovery is a real link: navigation still works if a script is interrupted.
      return;
    }
    const element = event.target.closest(checkoutSelector);
    if (!element) return;
    if (element.tagName === 'A') {
      const url = new URL(element.href, location.origin);
      if (url.origin !== location.origin || !/\/(checkout|checkouts)(\/|$)/.test(url.pathname)) return;
    }
    event.preventDefault(); event.stopImmediatePropagation();
    this.checkout();
  }

  change(event) {
    const input = event.target;
    if (!input.matches?.('[data-valr-quantity]')) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (this.recoveryRequired) { this.recover(); return; }
    if (this.busy) return;
    const controls = input.closest('[data-valr-controls]');
    const target = Number(input.value);
    if (!Number.isSafeInteger(target) || target < 0 || target > 999) {
      input.value = controls.dataset.quantity; return;
    }
    const identity = controls.dataset.identity;
    this.enqueue((cart, api) => changeGroup(cart, this.config, identity, target, api));
  }

  submit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.cart-form,form[action$="/cart"]')) return;
    if (event.submitter?.name !== 'checkout') return;
    event.preventDefault(); event.stopImmediatePropagation();
    this.checkout();
  }

  async idle(signal) {
    do {
      await this.waitNative(signal);
      await waitFor(this.tail, signal);
      if (this.scheduled) await waitFor(new Promise((resolve) => setTimeout(resolve, 0)), signal);
      checkSignal(signal);
    } while (this.tasks || this.pending.size || this.scheduled);
  }

  async checkout() {
    if (this.checkoutRequested) return;
    if (this.recoveryRequired) { this.recover(); return; }
    this.checkoutRequested = true;
    const timeout = deadline(TIMEOUTS.checkout, 'CHECKOUT_TIMEOUT');
    try {
      await this.idle(timeout.signal);
      await waitFor(this.enqueue(), timeout.signal);
      await this.idle(timeout.signal);
      if (this.result && !this.error && !this.recoveryRequired && !this.busy) {
        const url = new URL(this.root() + 'checkout', location.origin);
        location.assign(url.href);
      }
    } catch (error) {
      this.fail(error);
    } finally {
      timeout.dispose();
      this.checkoutRequested = false;
      this.paint();
    }
  }

  async prepareBuyNow(intent, signal) {
    checkSignal(signal);
    const cartPath = new URL(this.root() + 'cart', location.origin).pathname.replace(/\/$/, '');
    const url = new URL(`${cartPath}/${intent.variantId}:${intent.quantity}`, location.origin);
    url.searchParams.set('storefront', 'true');
    const timeout = deadline(TIMEOUTS.request, 'BUY_NOW_TIMEOUT', signal);
    try {
      // fetch follows Shopify's cart redirect without navigating the browser or
      // executing the returned page. Cancel its HTML body; only the cart cookie is needed.
      const response = await waitFor(fetch(url, {
        credentials: 'same-origin', cache: 'no-store', redirect: 'follow',
        headers: { Accept: 'text/html' }, signal: timeout.signal,
      }), timeout.signal);
      if (response.body) void response.body.cancel().catch(() => {});
      checkSignal(signal);
      const destination = new URL(response.url);
      if (!response.ok || destination.origin !== location.origin ||
          ![cartPath, url.pathname].includes(destination.pathname.replace(/\/$/, ''))) {
        throw cartError('The selected product could not be prepared.', 'BUY_NOW_FAILED');
      }
    } finally { timeout.dispose(); }
  }

  async buyNow(intent) {
    if (this.checkoutRequested) return false;
    if (this.recoveryRequired) throw cartError('Reload your cart before trying again.');
    if (!/^\d+$/.test(intent.variantId) || !Number.isSafeInteger(intent.quantity) || intent.quantity < 1) {
      throw cartError('Invalid product selection.');
    }
    this.checkoutRequested = true;
    const timeout = deadline(TIMEOUTS.checkout, 'CHECKOUT_TIMEOUT');
    try {
      await this.idle(timeout.signal);
      this.directBuyIntent = intent;
      const ready = await waitFor(this.enqueue(undefined, {
        signal: timeout.signal,
        prepare: (signal) => this.prepareBuyNow(intent, signal),
        render: false, // Checkout needs server cart data, not a rendered drawer.
      }), timeout.signal);
      await this.idle(timeout.signal);
      checkSignal(timeout.signal);
      if (!ready || !this.result || this.error || this.recoveryRequired) {
        throw cartError('The cart could not finish updating.');
      }
      this.verifyBuyNowCart(this.result.cart, intent);
      if (this.result.tier < this.result.eligible) {
        throw cartError('Some gifts could not be confirmed.', 'GIFT_UNAVAILABLE');
      }
      const url = new URL(this.root() + 'checkout', location.origin);
      location.assign(url.href);
      return true;
    } catch (error) {
      if (error.code !== 'GIFT_UNAVAILABLE') this.fail(error);
      throw error;
    } finally {
      timeout.dispose();
      this.directBuyIntent = null;
      this.checkoutRequested = false;
      this.setBusy(this.tasks > 0 || this.pending.size > 0 || this.scheduled);
    }
  }
}

if (!customElements.get('valr-cart-rewards')) {
  customElements.define('valr-cart-rewards', class extends HTMLElement {
    connectedCallback() {
      try {
        const config = JSON.parse(this.dataset.config);
        if (!config.enabled) { this.hidden = true; return; }
        if (!window.valrsoulRewards) {
          window.valrsoulRewards = new RewardsManager(config);
          window.valrsoulRewards.version = `${VERSION}+whole-gift-values-20261029`;
        }
        window.valrsoulRewards.mount(this, config);
      } catch (error) {
        console.warn('[VALRSOUL rewards]', error.message);
      }
    }
    disconnectedCallback() { window.valrsoulRewards?.views.delete(this); }
  });
}
